import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { Uploader } from 'plupload'
import { Button } from 'is24-corecss'

import { finishVideoUpload } from '../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { useAppDispatch } from '../../../../../../../../../../app/hooks'

import { UploadFile } from '../../../../../../../../../../@types/multimedia-types'
import { AttachmentType } from '../../../../../../../../../../@types/types'
import { showMultimediaMessage } from '../../../../../../../../../../redux/reducers/message/messageFeature'
import { trackingOnAttachmentUpload } from '../../../../../../../../../../tracking/tracking'

import { videoUploadTicket } from '../../../../../../../../../../api/multimedia/multimediaApi'
import {
  progressUploadAttachment,
  startUploadAttachmentRequest,
} from '../../../../../../../../../../redux/reducers/multimedia/multimediaFeature'
import { IconS24CameraVideo48 } from '@is24/cosma-ui-icons'
import styles from '../common/MultimediaButtonCommon.module.css'
import { useToastContext } from '../../../../../../../../../../app/toast/toastContext'
import MultimediaButtonContent from '../common/MultimediaButtonContent'
import { MultimediaButtonTypes } from '../common/mediaButtonContentProvider'
import { filenameIsValid } from '../../../../../../../../../../services/validation/rules/attachmentFileNameRule'

type PlUploadSettings = plupload_settings

export type MultimediaVideoButtonTypes = Omit<MultimediaButtonTypes, 'isFloorPlan'> & {
  realEstateId?: number
  disabled: boolean
}

const MultimediaVideoButton: React.FC<MultimediaVideoButtonTypes> = (props) => {
  const ref = useRef<HTMLButtonElement>(null)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const dropzoneId = uuid()
  const { addToast } = useToastContext()

  useEffect(() => {
    const realEstateId = props.realEstateId
    if (!realEstateId) {
      return
    }
    const settings = {
      runtimes: 'html5,html4',
      multi_selection: false,
      browse_button: ref.current,
      url: '/',
      file_data_name: 'file',
      send_file_name: false,

      init: {
        FilesAdded: async (up, files) => {
          const invalidFileName = files.some((file) => !filenameIsValid(file.name))
          if (invalidFileName) {
            dispatch(showMultimediaMessage({ message: 'attachmentFilename' }))
            files.forEach((file) => {
              up.removeFile(file)
            })
            return
          }

          const file: UploadFile = files[0]
          const videoTicket = await videoUploadTicket()
          file.videoId = videoTicket.videoId
          up.settings.url = videoTicket.uploadUrl
          up.start()
          up.refresh()
          dispatch(startUploadAttachmentRequest({ attachmentType: AttachmentType.VIDEO, file: file }))
          trackingOnAttachmentUpload(props.flowInformation, props.allowedFileType, props.entryMode)
        },
        UploadProgress: (up, file) => dispatch(progressUploadAttachment({ fileId: file.id, percent: file.percent })),
        PostInit: () => uploader.refresh(),
        FileUploaded: (up, file) => dispatch(finishVideoUpload(file, realEstateId)),
        Error: (_, error) => {
          if (error.code === -600) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentVideoFileSize',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else if (error.code === -601) {
            dispatch(
              showMultimediaMessage({
                message: 'attachmentFileType',
                values: {
                  filename: error.file.name,
                },
              })
            )
          } else {
            dispatch(showMultimediaMessage({ message: 'attachment' }))
          }
        },
      },
      drop_element: dropzoneId,
      filters: {
        max_file_size: '1024MB',
        mime_types: [
          { extensions: props.allowedFileType, title: t('wizard.screen.multimedia.supportedFiles', 'Supported Files') },
        ],
      },
      multipart_params: {},
    } as PlUploadSettings

    const uploader: Uploader = new Uploader(settings)
    uploader.init()
    uploader.refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.realEstateId])

  const tPrefix = `wizard.screen.multimediaNew.buttons.videos`
  const requiredFieldsNotFilledIn = !props.realEstateId
  const buttonDisabled = requiredFieldsNotFilledIn || props.disabled
  const buttonClassNames = `${buttonDisabled ? styles.buttonDisabled : 'cursor-pointer'} ${styles.button} align-left border-radius-rounded`

  const handleButtonClick = () => {
    if (buttonDisabled) {
      addToast({
        headline: t('wizard.screen.multimediaNew.toast.headline'),
        message: <>{t('wizard.screen.multimediaNew.toast.message')}</>,
        type: 'info',
      })
    }
  }

  return (
    <Button
      id={dropzoneId}
      ref={ref}
      className={buttonClassNames}
      onClick={handleButtonClick}
      data-testid='video-button-test-id'
    >
      <MultimediaButtonContent
        Icon={IconS24CameraVideo48}
        title={t(`${tPrefix}.title`)}
        subtitle={t(`${tPrefix}.fileTypes`)}
      />
    </Button>
  )
}

export default MultimediaVideoButton
